import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import autoUploadsAPI from '@/api/auto-uploads.api';
import { METADATA_FIELDS } from './auto-uploads/constants';
import { MetadataFieldsSelector } from './metadata-fields-selector';
import { formatNumber } from 'shared/utils';

export function OrgMetadataExtractionDialog({ open, onClose }) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFields, setSelectedFields] = useState(new Set(METADATA_FIELDS.map(f => f.id)));

  const handleQueueExtraction = async () => {
    if (selectedFields.size === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one metadata field',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await autoUploadsAPI.queueMetadataExtraction({
        extractAll: true,
        fields: Array.from(selectedFields),
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to queue metadata extraction');
      }

      const { tasksCreated } = response.data;

      toast({
        title: 'Metadata Extraction Started',
        description: `Metadata extraction queued for ${formatNumber(tasksCreated)} item${
          tasksCreated !== 1 ? 's' : ''
        }. Processing will happen in the background.`,
      });

      onClose();
    } catch (error) {
      console.error('Error queuing metadata extraction:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to queue metadata extraction',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Extract Metadata for All Items</DialogTitle>
          <DialogDescription>
            This will queue metadata extraction for all knowledge items in this organization.
          </DialogDescription>
        </DialogHeader>

        <MetadataFieldsSelector
          selectedFields={selectedFields}
          onFieldsChange={setSelectedFields}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleQueueExtraction} disabled={selectedFields.size === 0 || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Queuing...
              </>
            ) : (
              'Start Extraction'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
